import { SelfContainedCommand, CommandExecutionContext } from '../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../types/standardToolResults';
import { schemaToParameters } from '../../../utils/schemaToParameters';

export const tasks_write: SelfContainedCommand = {
  id: 'tasks_write',
  description: `Create or update a Task entity. Use for action items from triage, meeting prep, email processing, or any tracked work.

**CREATE vs UPDATE:**
- Omit taskId to create a new task
- Provide taskId to update an existing task (only provided fields are changed)

**PRIORITY LEVELS:** low | medium | high

**STATUS VALUES:** todo | in-progress | done

**EXAMPLES:**

Create a high-priority task from email triage:
{ "title": "Reply to Sarah re: contract renewal", "priority": "high", "dueDate": "2026-02-28", "notes": "From: sarah@acme.com — needs renewal decision by EOW", "tags": ["client", "contracts"] }

Create a simple todo:
{ "title": "Schedule team standup" }

Update an existing task's status:
{ "taskId": "task_review_contract_abc", "status": "done" }

Update a task with a due date:
{ "taskId": "task_review_contract_abc", "dueDate": "2026-03-01", "priority": "high" }`,
  autonomyLevel: 'auto',
  visualTreatment: 'silent',

  schema: {
    parameters: schemaToParameters({
      type: 'object',
      properties: {
        title: {
          type: 'string',
          description: 'Task title — concise action description',
        },
        dueDate: {
          type: 'string',
          description: 'Due date in ISO format YYYY-MM-DD. Omit if no deadline.',
        },
        priority: {
          type: 'string',
          enum: ['low', 'medium', 'high'],
          description: 'Task priority. Default: medium. Use high for urgent items.',
          default: 'medium',
        },
        status: {
          type: 'string',
          enum: ['todo', 'in-progress', 'done'],
          description: 'Task status. Default: todo',
          default: 'todo',
        },
        notes: {
          type: 'string',
          description: 'Additional context — sender, subject, what is needed, etc.',
        },
        tags: {
          type: 'array',
          items: { type: 'string' },
          description: 'Optional tags for categorization (e.g. ["client", "urgent"])',
        },
        taskId: {
          type: 'string',
          description: 'llmId of an existing Task to update. Omit to create a new task.',
        },
      },
      required: ['title'],
    }),
  },

  examples: { good: [], bad: [] },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const {
      title,
      dueDate,
      notes,
      tags = [],
      taskId,
    } = params;

    // status and priority are only used as defaults for creates, not blindly applied on updates
    const createStatus = params.status ?? 'todo';
    const createPriority = params.priority ?? 'medium';

    if (!title?.trim()) {
      return createErrorResult('Missing title', 'title is required', 'validation');
    }

    const today = new Date().toISOString().split('T')[0];
    const spaceId = context.spaceId;

    try {
      const { default: useEntityStore } = await import('../../../stores/entityStore');
      const store = useEntityStore.getState();

      if (taskId) {
        const entities: any[] = store.entities || [];
        const existing = entities.find((e: any) => e.llmId === taskId || e._id === taskId);
        if (!existing) {
          return createErrorResult('Task not found', `No task found with id: ${taskId}`, 'validation');
        }

        // Build skeleton patch — only override fields that were explicitly provided
        const skeletonPatch: Record<string, any> = { ...existing.skeleton };
        if (params.status !== undefined) skeletonPatch.status = params.status;
        if (params.priority !== undefined) skeletonPatch.priority = params.priority;
        if (dueDate !== undefined) skeletonPatch.dueDate = dueDate || null;
        if (notes !== undefined) skeletonPatch.notes = notes;
        if (tags.length > 0) skeletonPatch.tags = tags;

        // Sync completed with status
        if (skeletonPatch.status === 'done') {
          skeletonPatch.completed = true;
          if (!skeletonPatch.completedAt) {
            skeletonPatch.completedAt = new Date().toISOString();
          }
        } else if (skeletonPatch.status && skeletonPatch.status !== 'done') {
          skeletonPatch.completed = false;
          skeletonPatch.completedAt = undefined;
        }

        const updatePayload: Record<string, any> = { skeleton: skeletonPatch };
        // Update root name when title changes
        if (title.trim() !== existing.name) {
          updatePayload.name = title.trim();
        }

        await store.actions.updateEntity(existing._id, updatePayload);

        return createSuccessResult(`Updated task: "${title}"`, {
          taskId,
          entityId: existing._id,
          name: title,
        });
      }

      const created = await store.actions.createEntity({
        entityType: 'Task',
        spaceId,
        name: title.trim(),
        hidden: true,
        skeleton: {
          '@type': 'Task',
          status: createStatus,
          completed: createStatus === 'done',
          priority: createPriority,
          dueDate: dueDate || null,
          notes: notes || '',
          tags: tags || [],
          isInToday: dueDate === today,
          order: Date.now(),
        },
      });

      return createSuccessResult(`Created task: "${title}"`, {
        taskId: created?.llmId || created?._id,
        entityId: created?._id,
        name: created?.name || title,
      });
    } catch (error: any) {
      return createErrorResult(
        'Failed to write task',
        error.message || 'Unknown error',
        'execution'
      );
    }
  },
};
